import { useCallback } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useAudienceContext, useDialogs } from "@/context";
import { Audience, PageState } from "@/types";
import { shareAudience as shareAudienceAPI, updateAudienceName } from "@/api";



interface UseAudienceActionsProps {
  navigate: NavigateFunction;
  pageState: PageState;
}

export function useAudienceActions ({ navigate, pageState }: UseAudienceActionsProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();
  const { openDialog, closeDialog } = useDialogs();

  const handleShareAudience = async (
    audience: Audience,
    ids: number[]
  ) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await shareAudienceAPI(
      accessToken,
      audience.audience_id || "",
      ids
    );

    if (data) {
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      pageState.error.setError(error.message);
    }
  };

  const handleEditAudience = async (
    audienceId: string,
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();

    const { data, error } = await updateAudienceName(
      token,
      audienceId,
      audienceName,
      projectName
    );

    if (data) {
      dispatch({
        type: "CHANGE_NAME",
        payload: {
          audienceId,
          audienceName,
          projectName,
        },
      });
      closeDialog();
    }

    if (error) {
      pageState.error.setError(error.message);
      closeDialog();
    }
  };

  const createAudience = useCallback(
    () => {
      dispatch({ type: "CREATE_AUDIENCE" });
      navigate("new");
    },
    [dispatch, navigate]
  );

  const selectAudience = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(audience.audience_id);
    },
    [dispatch, navigate]
  );

  const viewInsights = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(`${audience.audience_id}/insight`);
    },
    [dispatch, navigate]
  );

  const exportAudience = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(`${audience.audience_id}/export`);
    },
    [dispatch, navigate]
  );

  const editAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "editAudience",
        props: {
          audienceId: audience.audience_id,
          audienceName: audience.audience_name,
          projectName: audience.project_name,
          onConfirm: handleEditAudience,
        },
      });
    },
    [openDialog]
  );

  const deleteAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "deleteAudience",
        props: {
          audience,
          redirect: "/audience",
        },
      });
    },
    [openDialog]
  );

  const shareAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "shareAudience",
        props: {
          selectedEntity: audience,
          onShare: async (ids: number[]) => {
            await handleShareAudience(audience, ids);
          },
        },
      });
    },
    [openDialog]
  );

  return {
    createAudience,
    selectAudience,
    viewInsights,
    exportAudience,
    editAudience,
    deleteAudience,
    shareAudience
  }
}
